import React, { useEffect, useRef, useState } from "react";
import { getBrandAppService } from "../services/BrandAppServices";
import { getUserData } from "../utils/localStorage";
import { Col, Container, Nav, Row } from "react-bootstrap";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import {
  AddPriceGarmentServiceUpload,
  getBrandPriceCode,
  postBrandPriceListConfigData,
} from "../services/getPriceCode";
import { getServiceUnit } from "../services/serviceUnit";
import { errorMessage, successMessage } from "../utils/alertMessage";
import { Link, useNavigate } from "react-router-dom";
import AdminMenu from "../pages/AdminMenu";
import {
  IndianRupee,
  Ruler,
  Layers,
  Download,
  UploadCloud,
  FileSpreadsheet,
  X,
  ArrowRight,
} from "lucide-react";

// ---- design tokens -------------------------------------------------------
const brand = "#1752AA";
const brandBg = "#EAF1FB";
const ink = "#1A2433";
const slate = "#5C6B7A";
const line = "#E1E6EE";
const paper = "#F5F7FB";
const danger = "#C0392B";

const styles = {
  page: { background: paper, minHeight: "calc(100vh - 3rem)", padding: "28px 0 48px" },
  wrap: { maxWidth: 1080, margin: "0 auto", padding: "0 16px" },
  card: {
    background: "#fff",
    border: `1px solid ${line}`,
    borderRadius: 14,
    padding: 24,
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
  },
  cardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  step: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: brand,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    marginRight: 10,
    flexShrink: 0,
  },
  cardTitle: { margin: 0, fontSize: 18, fontWeight: 600, color: ink, display: "flex", alignItems: "center" },
  cardSub: { margin: "6px 0 0 36px", fontSize: 14, color: slate },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20,
  },
  label: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: ink, marginBottom: 8 },
  iconTile: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 8,
    background: brandBg,
    color: brand,
  },
  error: { color: danger, fontSize: 13, marginTop: 6, minHeight: 18 },
  primaryBtn: (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    borderRadius: 8,
    border: "none",
    background: disabled ? "#9DB3D6" : brand,
    color: "#fff",
    fontWeight: 600,
    fontSize: 15,
    cursor: disabled ? "not-allowed" : "pointer",
  }),
  outlineBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 16px",
    borderRadius: 8,
    border: `1px solid ${brand}`,
    background: "#fff",
    color: brand,
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  dropzone: (disabled, dragging) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "32px 16px",
    borderRadius: 12,
    border: `2px dashed ${dragging ? brand : line}`,
    background: disabled ? "#FAFBFC" : dragging ? brandBg : "#fff",
    color: disabled ? "#A0AAB6" : slate,
    textAlign: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 0.15s, border-color 0.15s",
  }),
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${line}`,
    background: paper,
    marginTop: 14,
  },
};

const socialLinks = [
  { to: "https://www.youtube.com/channel/UCKdUWjGim18SoHC2jLbFsiw", icon: "pi-youtube", color: "red" },
  { to: "https://www.instagram.com/dhobicart/?hl=en", icon: "pi-instagram", color: "#d62976" },
  { to: "https://www.linkedin.com/company/71974228/admin/dashboard/", icon: "pi-linkedin", color: "#0072b1" },
  { to: "https://www.facebook.com/oneindiatechno", icon: "pi-facebook", color: "#1877F2" },
  { to: "https://play.google.com/store/apps/details?id=com.dhobicartV2", icon: "pi-android", color: "#a4c639" },
  { to: "tel:+91 9319518337", icon: "pi-phone", color: "#1877F2" },
];

const PriceListSelection = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const userData = getUserData();
  let brandId = userData.brandProfileData.id;
  const [brandPriceList, setBrandPriceList] = useState([]);
  const [serviceUnitList, setServiceUnitList] = useState([]);
  const [appServiceList, setAppServiceList] = useState([]);
  const [submitButtonPressed, setSubmitButtonPressed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [input, setInput] = useState({
    appServiceList: "",
    serviceUnit: "",
    priceId: "",
  });
  const [errors, setErrors] = useState({
    appServiceList: "",
    serviceUnit: "",
    priceId: "",
  });

  const uploadEnabled = Boolean(input.priceId && input.serviceUnit);

  const fetchAppService = () => {
    getBrandAppService(brandId)
      .then((response) => {
        if (!response.data.error) {
          const filteredData = response.data.filter(
            (service) => service.status === true,
          );
          const res = filteredData.map((service) => ({
            label: service.appService.name,
            value: service.appService.name,
          }));
          setAppServiceList(res);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  const fetchGetBrandPriceCode = () => {
    getBrandPriceCode(brandId)
      .then((response) => {
        if (!response.data.error) {
          const filteredData = response.data.prices.filter(
            (price) => price.status === true,
          );
          const priceList = filteredData.map((item) => ({
            label: item.priceCode,
            value: item.id,
          }));
          setBrandPriceList(priceList);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  const fetchGetServiceUnit = () => {
    getServiceUnit()
      .then((response) => {
        if (!response.data.error) {
          const res = response.data
            .filter((item) => item !== "Kg")
            .map((item) => ({
              label: item === "Qty" ? "Piece" : item, // Rename "Qty" to "Piece"
              value: item,
            }));
          setServiceUnitList(res);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  useEffect(() => {
    fetchAppService();
    fetchGetBrandPriceCode();
    fetchGetServiceUnit();
  }, []);

  const updateField = (field, value) => {
    setInput({ ...input, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: "" });
  };

  const validateForm = () => {
    const newErrors = {
      priceId: input.priceId === "" ? "Please select a tariff list" : "",
      serviceUnit: input.serviceUnit === "" ? "Please select a service unit" : "",
      appServiceList:
        input.appServiceList === "" || input.appServiceList.length === 0
          ? "Please select at least one app service"
          : "",
    };
    setErrors(newErrors);
    return !newErrors.priceId && !newErrors.serviceUnit && !newErrors.appServiceList;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setSubmitButtonPressed(true);
    postBrandPriceListConfigData(brandId, input)
      .then((response) => {
        if (response.data.error) return;
        if (response.data.success === false) {
          errorMessage(response.data.message);
          return;
        }
        successMessage(response.data.message);
        const selectedPrice = brandPriceList.find(
          (item) => item.value === input.priceId,
        );
        navigate("/my-tariff-list", {
          state: {
            ...response.data,
            selectedPriceCode: selectedPrice ? selectedPrice.label : "",
            type: "price",
          },
        });
      })
      .catch(() => {
        errorMessage();
      })
      .finally(() => setSubmitButtonPressed(false));
  };

  const handlePriceListDownload = () => {
    const link = document.createElement("a");
    link.href = "/garment_service_price.xlsx"; // File path inside public folder
    link.download = "garment_service_price.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pickFile = (file) => {
    if (!file) return;
    setUploadFile(file);
  };

  const clearFile = () => {
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!uploadEnabled) return;
    pickFile(event.dataTransfer.files[0]);
  };

  const handlePriceGarmentUpload = () => {
    if (!uploadFile || !uploadEnabled) return;

    setUploading(true);
    AddPriceGarmentServiceUpload(uploadFile, input.priceId, input.serviceUnit)
      .then((response) => {
        if (response.status === 200 && !response.data?.error && response.data?.success !== false) {
          successMessage("Garment prices uploaded successfully");
          clearFile();
          navigate("/");
        } else {
          errorMessage(response.data?.message || "Garment price upload failed");
        }
      })
      .catch(() => {
        errorMessage("Garment price upload failed");
      })
      .finally(() => setUploading(false));
  };

  return (
    <div className="flex-1">
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{ "--surface-0": brand }}
      >
        <div className="flex-1 flex align-items-center ml-3 mr-3">
          <i
            className="pi pi-angle-left mx-3"
            onClick={() => navigate("/TariffManagement")}
            style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
          ></i>
          <p className="flex-1 ml-4 m-0 text-2xl text-white font-bold uppercase">
            Tariff Management
          </p>
          <i
            className="pi pi-bars"
            onClick={() => setVisible(true)}
            style={{ fontSize: "27px", marginRight: 30, color: "#fff", cursor: "pointer" }}
          ></i>
        </div>
      </div>

      <div style={styles.page}>
        <div style={styles.wrap}>
          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: "6px 0 0", color: '#000', fontSize: 15, fontWeight:600 }}>
              Pick a tariff and service unit, then link it to your app services or bulk upload garment prices.
            </p>
          </div>

          {/* Step 1: configuration */}
          <form onSubmit={handleSubmit} style={styles.card}>
            <div style={styles.cardHead}>
              <div>
                <h4 style={styles.cardTitle}>
                  <span style={styles.step}>1</span>
                  Configure tariff
                </h4>
                <p style={styles.cardSub}>All three fields are required.</p>
              </div>
            </div>

            <div style={styles.grid}>
              <div>
                <label style={styles.label}>
                  <span style={styles.iconTile}><IndianRupee size={16} /></span>
                  Tariff List
                </label>
                <Dropdown
                  value={input.priceId}
                  options={brandPriceList}
                  onChange={(e) => updateField("priceId", e.value)}
                  placeholder="Select a tariff list"
                  filter={brandPriceList.length > 8}
                  className={errors.priceId ? "p-invalid" : ""}
                  style={{ width: "100%" }}
                />
                <div style={styles.error}>{errors.priceId}</div>
              </div>

              <div>
                <label style={styles.label}>
                  <span style={styles.iconTile}><Ruler size={16} /></span>
                  Service Unit
                </label>
                <Dropdown
                  value={input.serviceUnit}
                  options={serviceUnitList}
                  onChange={(e) => updateField("serviceUnit", e.value)}
                  placeholder="Select a service unit"
                  className={errors.serviceUnit ? "p-invalid" : ""}
                  style={{ width: "100%" }}
                />
                <div style={styles.error}>{errors.serviceUnit}</div>
              </div>

              <div>
                <label style={styles.label}>
                  <span style={styles.iconTile}><Layers size={16} /></span>
                  App Services
                </label>
                <MultiSelect
                  display="chip"
                  value={input.appServiceList}
                  options={appServiceList}
                  onChange={(e) => updateField("appServiceList", e.value)}
                  placeholder="Select app services"
                  className={errors.appServiceList ? "p-invalid" : ""}
                  style={{ width: "100%" }}
                />
                <div style={styles.error}>{errors.appServiceList}</div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button
                type="submit"
                style={styles.primaryBtn(submitButtonPressed)}
                disabled={submitButtonPressed}
              >
                {submitButtonPressed ? "Saving..." : "Save & Continue"}
                {!submitButtonPressed && <ArrowRight size={16} />}
              </button>
            </div>
          </form>

          {/* Step 2: bulk upload */}
          <div style={styles.card}>
            <div style={styles.cardHead}>
              <div>
                <h4 style={styles.cardTitle}>
                  <span style={styles.step}>2</span>
                  Bulk upload garment prices
                  <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 500, color: slate }}>(optional)</span>
                </h4>
                <p style={styles.cardSub}>
                  Fill in the sample sheet and upload it for the selected tariff and service unit.
                </p>
              </div>
              <button type="button" onClick={handlePriceListDownload} style={styles.outlineBtn}>
                <Download size={16} />
                Download sample file
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => pickFile(e.target.files[0])}
              style={{ display: "none" }}
            />

            {!uploadFile && (
              <div
                role="button"
                tabIndex={uploadEnabled ? 0 : -1}
                style={styles.dropzone(!uploadEnabled, dragging)}
                onClick={() => uploadEnabled && fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (uploadEnabled && (e.key === "Enter" || e.key === " ")) fileInputRef.current?.click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (uploadEnabled) setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
              >
                <UploadCloud size={36} color={uploadEnabled ? brand : "#A0AAB6"} />
                {uploadEnabled ? (
                  <>
                    <span style={{ fontWeight: 600, color: ink }}>
                      Click to choose a file or drag it here
                    </span>
                    <span style={{ fontSize: 13 }}>Excel (.xlsx, .xls) or CSV</span>
                  </>
                ) : (
                  <span style={{ fontSize: 14 }}>
                    Select a <strong>Tariff List</strong> and <strong>Service Unit</strong> above to enable upload
                  </span>
                )}
              </div>
            )}

            {uploadFile && (
              <>
                <div style={styles.fileRow}>
                  <FileSpreadsheet size={28} color="#1D7044" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: ink,
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {uploadFile.name}
                    </div>
                    <div style={{ fontSize: 12, color: slate }}>
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearFile}
                    disabled={uploading}
                    aria-label="Remove file"
                    style={{ border: "none", background: "transparent", color: slate, cursor: "pointer", display: "flex" }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={handlePriceGarmentUpload}
                    style={styles.primaryBtn(uploading || !uploadEnabled)}
                    disabled={uploading || !uploadEnabled}
                  >
                    <UploadCloud size={16} />
                    {uploading ? "Uploading..." : "Upload prices"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* <footer style={{ backgroundColor: "#FBFBFB", borderTop: `1px solid ${line}` }}>
        <Container fluid>
          <Row>
            <Col>
              <Nav className="flex social-media-div justify-content-center gap-3 pt-3">
                {socialLinks.map((s) => (
                  <Link key={s.icon} to={s.to} target="_blank">
                    <i className={`pi ${s.icon}`} style={{ fontSize: "1.6rem", color: s.color }}></i>
                  </Link>
                ))}
              </Nav>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="text-center p-3" style={{ fontSize: 14, color: slate }}>
                &copy; {new Date().getFullYear()} Copyright:{" "}
                <a className="text-dark" href="https://oneindiatech.com/">
                  One India Technologies Pvt. Ltd.
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </footer> */}
      <AdminMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default PriceListSelection;
